"use strict";
const _ = require("lodash");
const IconRegistry = require("@gluenetworks/icon_registry").getInstance();

/**
 * The base class of all icons.
 */
class BaseIcon extends HTMLElement {
    constructor() {
        super();
        //createdCallback is not used in the window.customElements.define, so we call it in the constructor()
        this.createdCallback();
    }

    /**
     * Custom Element lifecycle callback for the "created" event when the element is instantiated.
     */
    createdCallback() {
        //check if we already called createdCallback(), it added just to painless migration from document.registerElement to window.customElements.define
        if (
            typeof this._darkTheme === "undefined" &&
            typeof this._theme === "undefined" && // jshint ignore:line
            typeof this._isShown === "undefined"
        ) {
            // jshint ignore:line
            this._darkTheme = false;
            this._theme = false;
            this._isShown = true;
        }
    }

    /**
     * connectedCallback is used in window.customElements.define,
     * It called every time the element is inserted into the DOM
     */
    connectedCallback() {
        this.attachedCallback();
    }

    /**
     * Custom Element lifecycle callback for the "attached" event when the element is added to the DOM.
     */
    attachedCallback() {
        if (this.hasAttribute("data-dark-theme")) {
            let darkTheme = this.getAttribute("data-dark-theme");
            if (darkTheme === "true" || darkTheme === "") {
                this.setDarkTheme(true);
            }
        }
        if (this.hasAttribute("data-theme")) {
            let theme = this.getAttribute("data-theme");
            if (theme) {
                this.setTheme(theme);
            }
        }
        if (this.hasAttribute("data-hidden")) {
            this.hide();
        }
        if (this.hasAttribute("data-invalid")) {
            this.setInvalid(true);
        }
        this._loadIcon();
    }

    /**
     * Array containing the names of the attributes you want to observe
     */
    static get observedAttributes() {
        return ["data-icon"];
    }

    /**
     * Custom Element lifecycle callback for the "attributeChanged" event when any attribute on the DOM
     * element has been modified.
     * !notice: the attribute should be in the observedAttributes() array
     * @param {String} attrName - The name of the attribute that has been modified.
     * @param {String} oldVal - The previous value of the changed attribute.
     * @param {String} newVal - The new value of the changed attribute.
     */
    attributeChangedCallback(attrName, oldVal, newVal) {
        if (attrName === "data-icon") {
            if (!_.isEqual(oldVal, newVal)) {
                this._loadIcon();
            }
        }
    }

    /**
     * Custom Element lifecycle callback for the "detached" event when the element is removed from the DOM.
     */
    detachedCallback() {}

    /**
     * window.customElements.define - when the element is removed from the DOM
     */
    disconnectedCallback() {
        this.detachedCallback();
    }

    _loadIcon() {
        let iconNamespace = this.dataset.icon;
        if (iconNamespace) {
            let iconSvg = IconRegistry.getRegisteredIcon(iconNamespace);
            if (iconSvg && iconSvg.icon) {
                this.innerHTML = iconSvg.icon;
            } else {
                let err = "Invalid Icon Reference: " + iconNamespace;
                console.error(err);
                this.innerHTML = IconRegistry.getRegisteredIcon("common.skull").icon;
                this.setAttribute("title", err);
            }
        } else {
            let err = "Missing data-icon attribute.";
            console.error(err);
            this.innerHTML = IconRegistry.getRegisteredIcon("common.skull").icon;
            this.setAttribute("title", err);
        }
    }

    /***************************************/
    /* START THEME METHODS */
    /***************************************/

    get darkTheme() {
        return this._darkTheme;
    }

    set darkTheme(dark) {
        this.setDarkTheme(dark);
    }

    setDarkTheme(dark) {
        if (dark === true) {
            this._darkTheme = true;
            this.classList.add("dark");
        } else {
            this._darkTheme = false;
            this.classList.remove("dark");
        }
    }

    get theme() {
        return this._theme;
    }

    set theme(dark) {
        this.setTheme(dark);
    }

    setTheme(theme) {
        let oldTheme = this._theme;
        this._theme = theme;
        if (theme) {
            this.classList.add(theme);
        }
        if (oldTheme) {
            this.classList.remove(oldTheme);
        }
    }
    isDarkTheme() {
        return this._darkTheme;
    }

    isInvalid() {
        return this.getInvalid();
    }
    get invalid() {
        return this.getInvalid();
    }

    set invalid(bool) {
        this.setInvalid(bool);
    }

    getInvalid() {
        return this._invalid;
    }

    setInvalid(bool) {
        if (bool) {
            this._invalid = true;
            this.classList.add("invalid");
        } else {
            this._invalid = false;
            this.classList.remove("invalid");
        }
    }

    /***************************************/
    /* END THEME METHODS */
    /***************************************/
    hide() {
        this._isShown = false;
        this.classList.remove("shown");
        this.classList.add("hidden");
    }
    show() {
        this._isShown = true;
        this.classList.remove("hidden");
        this.classList.add("shown");
    }
    registerIcon(fullyQualifiedName, icon) {
        return IconRegistry.registerIcon(fullyQualifiedName, icon);
    }
    getRegisteredIcon(fullyQualifiedName) {
        return IconRegistry.getRegisteredIcon(fullyQualifiedName);
    }
    getAllRegisteredIcons() {
        return IconRegistry.getAllRegisteredIcons();
    }
    getAllRegisteredIconsWith() {
        let args = arguments;
        if (arguments.length === 1 && _.isArray(arguments[0])) {
            args = arguments[0];
        }
        return IconRegistry.getAllRegisterdIconsWith.apply(IconRegistry, args);
    }
    getCompatibleIcons() {
        return this.getAllRegisteredIconsWith("icon");
    }
}

let icon,
    customElementRegistry = window.customElements;

if (customElementRegistry) {
    icon = customElementRegistry.get("base-icon-gl");
    if (!icon) {
        customElementRegistry.define("base-icon-gl", BaseIcon);
        icon = customElementRegistry.get("base-icon-gl");
    }
} else if (document.registerElement) {
    icon = document.registerElement("base-icon-gl", BaseIcon);
} else {
    icon = BaseIcon;
    console.warn(
        "Your browser may not be supported. To run Gluware Control, we recommend using the latest version of Google Chrome."
    );
}

module.exports = icon;
